import moment from 'moment';
import React from 'react';
import Layout from '../../components/Layout';
import Publication from './Publication';
import categoryMap from '../../common/categoryMap';

const COMPANY_FILTERS = {
  1: 'BKT',
  4: 'LT',
  5: 'SST',
  6: 'SEAB',
  7: 'SEAC',
};

function getImageURL(uid) {
  if (!uid) return undefined;
  const imageURI =
    uid.indexOf('https') !== -1
      ? uid
      : `https://dxfpracgmlczp.cloudfront.net/${uid}`;
  return encodeURI(imageURI);
}

function fetchUser(id, fetch) {
  return new Promise(resolve => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `{users(id:${id}){ 
          items {
            id, first_name, last_name, email
          }
        }}`,
      }),
    })
      .then(res => res.json())
      .then(body => {
        if (body && body.data && body.data.users && body.data.users.items) {
          resolve(body.data.users.items[0]);
        } else {
          console.error(
            'No data found for publication body.data.users.items[0]',
          );
          resolve([]);
        }
      });
  });
}

function fetchPublicationAndRoles(id, fetch) {
  return new Promise((resolve, reject) => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `{publications(id:${id}){id, name, banner_image_uid, mast_head_uid, write_up, short_name, editor_id, group_marketing_manager_id, youtube_playlist_id}}`,
      }),
    })
      .then(res => res.json())
      .then(({ data }) => {
        if (data.publications.length === 0) {
          console.error(
            `[WARN][PUB-MAIN]: user access publication page with pubId ${id}`,
          );
          reject(new Error('No such publication'));
          return;
        }
        const publication = data.publications[0];
        Promise.all([
          fetchUser(publication.editor_id, fetch),
          fetchUser(publication.group_marketing_manager_id, fetch),
        ])
          .then(results => {
            resolve({
              publication,
              editor: results[0],
              associatePublisher: results[1],
            });
          })
          .catch(err => {
            throw err;
          });
      });
  });
}

function fetchIssues(id, fetch) {
  return new Promise(resolve => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `{issues(publication_id:${id} active:true){ items { id, thumbnail_uid, file_uid, name, release_date}}}`,
      }),
    })
      .then(res => res.json())
      .then(({ data }) => {
        // console.log('data', data);
        const issues = data && data.issues && data.issues.items;
        // console.log('issues', issues);
        resolve(issues);
      });
  });
}

function fetchAllNews(id, fetch) {
  return new Promise(resolve => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `query AllNews($publications: String) {
          news(currentIndex: 0, pageSize: 43, publications: $publications, active: true) {
            items {
              title date id author blurb blurb_image_uid blurb_expanded_image_uid
              article article_image_uid article_expanded_image_uid article_image_caption
              publications tags category
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
              totalCount
              totalPages
              currentIndex
            }
          }
        }`,
        variables: { publications: String(id) },
      }),
    })
      .then(res => res.json())
      .then(({ data }) => {
        const news = data && data.news && data.news.items;
        resolve(news);
      });
  });
}

function fetchNewsByCat(id, cat, fetch) {
  const catName = categoryMap[cat].name;
  return new Promise(resolve => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `query NewsByCat($publications: String, $category: String) {
          news(currentIndex: 0, pageSize: 43, publications: $publications, category: $category, active: true) {
            items {
              title date id author blurb blurb_image_uid blurb_expanded_image_uid
              article article_image_uid article_expanded_image_uid article_image_caption
              publications tags category
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
              totalCount
              totalPages
              currentIndex
            }
          }
        }`,
        variables: { publications: String(id), category: catName },
      }),
    })
      .then(res => res.json())
      .then(({ data }) => {
        const news = data && data.news && data.news.items;
        resolve(news);
      });
  });
}

function fetchEvents(id, fetch) {
  const startDate = moment(); // first day of the month
  const endDate = moment(startDate).add(2, 'years');

  return new Promise(resolve => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `{ eventsBetween(
          start_date: "${startDate.format('YYYY-MM-DD')} 00:00:00", 
          end_date: "${endDate.format('YYYY-MM-DD')} 23:59:59",
          publications: "${id}"){ items {
          id, title, banner_image_uid, url, start_date, end_date, location, organization, organization_url,
          position, created_at, updated_at, comments, publications}}}`,
      }),
    })
      .then(res => res.json())
      .then(({ data }) => {
        const events = data && data.eventsBetween && data.eventsBetween.items;
        resolve(events);
      });
  });
}

function fetchAds(id, fetch) {
  return new Promise(resolve => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `{ ads(publications: "${id}", active:true, pageSize:100, currentIndex:0){
                  items{
                    id, title, image_uid, image_url, type, position
                  }
                  pageInfo {
                    hasNextPage
                    hasPreviousPage
                    totalCount
                    totalPages
                    currentIndex
                   }
                }}`,
      }),
    })
      .then(res => res.json())
      .then(({ data }) => {
        resolve(data.ads.items.filter(ad => ad.image_uid));
      });
  });
}

function fetchNews(id, cat, fetch) {
  if (cat) return fetchNewsByCat(id, cat, fetch);
  return fetchAllNews(id, fetch);
}

function fetchSuppliers(fetch) {
  return new Promise(resolve => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `{suppliers{items{id telephone fax company description email website country countryname publications}}}`,
      }),
    })
      .then(res => res.json())
      .then(body => {
        body.data.suppliers.items.forEach(s => {
          if (s.publications !== null) {
            const pubs = s.publications.split(',');
            const newPubs = pubs.map(p => COMPANY_FILTERS[p]);
            // eslint-disable-next-line no-param-reassign
            s.publications = newPubs.join(',');
          }
        });
        resolve(body.data.suppliers.items);
      });
  });
}

function fetchVideos(id, fetch) {
  return new Promise(resolve => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `{ videos(publicationId: "${id}", pageSize:30){
          items{
                id, title, video_uid, image_url, description, created_at
              }
          pageInfo {
            hasNextPage
            hasPreviousPage
            totalCount
            totalPages
            currentIndex
           }
        }}`,
      }),
    })
      .then(res => res.json())
      .then(body => {
        // console.log(body.data.videos.items);
        resolve(
          (body.data && body.data.videos && body.data.videos.items) || [],
        );
      });
  });
}

function fetchIndustrialLinks(fetch) {
  return new Promise(resolve => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `{industrialLinks{ items { id organization abbreviation website publications}}}`,
      }),
    })
      .then(res => res.json())
      .then(body => {
        body.data.industrialLinks.items.forEach(s => {
          if (s.publications !== null) {
            const pubs = s.publications.split(',');
            const newPubs = pubs.map(p => COMPANY_FILTERS[p]);
            // eslint-disable-next-line no-param-reassign
            s.publications = newPubs.join(',');
          }
        });
        resolve(body.data.industrialLinks.items);
      });
  });
}

async function action(context) {
  const { fetch, pathname } = context;
  const { id, cat } = context.params;
  let inputProps = {};

  // safeguard for invalid params in URL
  if (!/^\d*$/.test(id)) {
    console.error(
      `[WARN][PUB-MAIN]: user access publication page with pubId ${id}`,
    );
    return { redirect: '/not-found' };
  }

  // /news/category/all is a SOFT 404: fetchNewsByCat queries category="All",
  // no article is filed under that name, and the page renders 200 with zero
  // articles. The UI never produces this URL — Publication.js maps the "all"
  // tab back to /publications/:id/ — but a crawler that assembles it from the
  // category list gets an empty page it may index or count against the site.
  // Send it where the UI already points, permanently.
  if (cat === 'all') {
    return { redirect: `/publications/${id}/`, status: 301 };
  }

  try {
    await new Promise((resolve, reject) => {
      Promise.all([
        fetchPublicationAndRoles(id, fetch),
        fetchIssues(id, fetch),
        fetchNews(id, cat, fetch),
        fetchEvents(id, fetch),
        fetchAds(id, fetch),
        fetchIndustrialLinks(fetch),
        fetchSuppliers(fetch),
        fetchVideos(id, fetch),
      ])
        .then(results => {
          inputProps = {
            publication: results[0].publication,
            editor: results[0].editor,
            associatePublisher: results[0].associatePublisher,
            issues: results[1],
            news: results[2],
            events: results[3],
            ads: results[4],
            industrialLinks: results[5],
            suppliers: results[6],
            videos: results[7],
            cat,
            fetch,
            onFetchNews: (i, c) => fetchNews(i, c, fetch),
          };
          resolve('Successfully fetched data');
        })
        .catch(err => {
          reject(err);
        });
    });
  } catch (err) {
    return { redirect: '/not-found' };
  }

  const { publication, issues } = inputProps;
  const latestIssueThumb =
    (issues && issues.length > 0 && issues[0].thumbnail_uid) ||
    (publication && publication.mast_head_uid);
  const pubName = (publication && publication.name) || 'Publication';
  const description =
    (publication && publication.description) ||
    `${pubName} — latest issues, news and industry updates from TradeLink Media.`;

  return {
    chunks: ['publication'],
    title: pubName,
    description,
    image: getImageURL(latestIssueThumb),
    site:
      publication &&
      publication.name &&
      `@${publication.name.replace(/ /g, '')}`,
    card: 'summary_large_image',
    url:
      publication && publication.short_name
        ? `https://${publication.short_name}.tradelinkmedia.biz${pathname}`
        : undefined,
    component: (
      <Layout publication={inputProps.publication} ads={inputProps.ads}>
        <Publication {...inputProps} />
      </Layout>
    ),
  };
}

export default action;
